import {
	CheckCircle,
	ChevronDown,
	ChevronUp,
	FlipBackward,
} from "@untitledui/icons";
import {
	type PointerEvent as ReactPointerEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { ConfirmModal } from "@/components/application/modals/confirm-modal";
import { cx } from "@/utils/cx";
import {
	DAMAGE_ANNOTATION_TYPES,
	type DamageAnnotationType,
	type DamageMarker,
	DEFAULT_DAMAGE_ANNOTATION_TYPE,
	EXPORT_JPEG_QUALITY,
	EXPORT_MAX_LONG_EDGE,
	TAP_SLOP_PX,
} from "../constants/damage-annotation.constants";
import type { AnnotatedFile } from "../types";
import {
	bakeAnnotatedJpeg,
	drawDamageRect,
} from "../utils/draw-damage-annotation";
import {
	clampScreenPointToVisibleImageContain,
	getContainTransform,
	imageRectToScreenContain,
	normalizeScreenDraft,
	type Point,
	pointInImageRect,
	type Size,
	screenRectToImageRectContain,
	screenToImageCoordsContain,
} from "../utils/image-display-bounds";
import { LandscapeWrapper } from "./landscape";

type ImageAnnotationProps = {
	img: Blob;
	onRetake: () => void;
	onSave: (file: AnnotatedFile) => void;
	fileName: string;
	onInteraction: () => void;
	isLandscape: boolean;
	onExit: () => void;
};

type ScreenDraft = {
	start: Point;
	end: Point;
};

type InteractionMode = "draw" | "select" | null;

const DELETE_PROMPT_WIDTH = 150;
const DELETE_PROMPT_HEIGHT = 36;
const DELETE_PROMPT_GAP = 8;
const DELETE_PROMPT_EDGE = 8;

function createAnnotatedFile(
	blob: Blob,
	fileName: string,
	damages: number[]
): AnnotatedFile {
	const file = new File([blob], fileName, {
		type: blob.type || "image/jpeg",
	});

	return Object.assign(file, {
		uri: URL.createObjectURL(file),
		damages,
	});
}

function ignoreAsyncError(): undefined {
	return;
}

function getImageSize(image: HTMLImageElement): Size {
	return { width: image.width, height: image.height };
}

function isTap(start: Point, end: Point): boolean {
	return Math.hypot(end.x - start.x, end.y - start.y) < TAP_SLOP_PX;
}

function createMarkerFromDraft(params: {
	start: Point;
	end: Point;
	container: Size;
	imageSize: Size;
	type: DamageAnnotationType;
}): DamageMarker | null {
	if (isTap(params.start, params.end)) {
		return null;
	}

	const imageRect = screenRectToImageRectContain(
		params.start,
		params.end,
		params.container,
		params.imageSize
	);
	if (!imageRect) {
		return null;
	}

	return {
		id: crypto.randomUUID(),
		...imageRect,
		damageType: params.type.damageType,
		letter: params.type.type,
		color: params.type.color,
	};
}

function getCanvasPoint(
	event: ReactPointerEvent<HTMLCanvasElement>,
	canvas: HTMLCanvasElement
): Point {
	const rect = canvas.getBoundingClientRect();
	return {
		x: event.clientX - rect.left,
		y: event.clientY - rect.top,
	};
}

function findMarkerAtImage(
	markers: DamageMarker[],
	point: Point
): DamageMarker | null {
	for (const marker of [...markers].reverse()) {
		if (pointInImageRect(point, marker)) {
			return marker;
		}
	}
	return null;
}

function getDeletePromptPosition(
	screen: {
		x: number;
		y: number;
		width: number;
		height: number;
	},
	container: Size
): Point {
	let left = screen.x + screen.width + DELETE_PROMPT_GAP;
	if (left + DELETE_PROMPT_WIDTH > container.width - DELETE_PROMPT_EDGE) {
		left = screen.x - DELETE_PROMPT_WIDTH - DELETE_PROMPT_GAP;
	}

	const top = screen.y + screen.height / 2 - DELETE_PROMPT_HEIGHT / 2;

	return {
		x: Math.max(
			DELETE_PROMPT_EDGE,
			Math.min(left, container.width - DELETE_PROMPT_WIDTH - DELETE_PROMPT_EDGE)
		),
		y: Math.max(
			DELETE_PROMPT_EDGE,
			Math.min(
				top,
				container.height - DELETE_PROMPT_HEIGHT - DELETE_PROMPT_EDGE
			)
		),
	};
}

export const ImageAnnotation = ({
	img,
	onRetake,
	onSave,
	fileName,
	onInteraction,
	isLandscape,
	onExit,
}: ImageAnnotationProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const imageRef = useRef<HTMLImageElement | null>(null);
	const markersRef = useRef<DamageMarker[]>([]);
	const selectedTypeRef = useRef<DamageAnnotationType>(
		DEFAULT_DAMAGE_ANNOTATION_TYPE
	);
	const containerSizeRef = useRef<Size>({ width: 0, height: 0 });
	const interactionRef = useRef<InteractionMode>(null);
	const startScreenRef = useRef<Point>({ x: 0, y: 0 });
	const pendingSelectIdRef = useRef<string | null>(null);
	const isLandscapeRef = useRef(isLandscape);

	const [markers, setMarkers] = useState<DamageMarker[]>([]);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [draftScreen, setDraftScreen] = useState<ScreenDraft | null>(null);
	const [selectedType, setSelectedType] = useState<DamageAnnotationType>(
		DEFAULT_DAMAGE_ANNOTATION_TYPE
	);
	const [isSaving, setIsSaving] = useState(false);
	const [isImageReady, setIsImageReady] = useState(false);
	const [legendCollapsed, setLegendCollapsed] = useState(false);
	const [isExitModalOpen, setIsExitModalOpen] = useState(false);
	const [containerSize, setContainerSize] = useState<Size>({
		width: 0,
		height: 0,
	});

	markersRef.current = markers;
	selectedTypeRef.current = selectedType;
	containerSizeRef.current = containerSize;
	isLandscapeRef.current = isLandscape;

	const enableFullscreenFallback = useCallback(() => {
		document.body.style.overflow = "hidden";
		document.documentElement.style.overflow = "hidden";
	}, []);

	const disableFullscreenFallback = useCallback(() => {
		document.body.style.overflow = "initial";
		document.documentElement.style.overflow = "initial";
		document.body.style.height = "";
		document.documentElement.style.height = "";
	}, []);

	useEffect(
		() => () => {
			disableFullscreenFallback();
		},
		[disableFullscreenFallback]
	);

	const enterFullscreen = useCallback(async () => {
		const target = containerRef.current ?? document.documentElement;
		type FullscreenDocument = Document & {
			webkitFullscreenElement?: Element | null;
			mozFullScreenElement?: Element | null;
			msFullscreenElement?: Element | null;
		};
		type FullscreenElement = HTMLElement & {
			webkitRequestFullscreen?: () => Promise<void>;
			mozRequestFullScreen?: () => Promise<void>;
			msRequestFullscreen?: () => Promise<void>;
		};

		const doc = document as FullscreenDocument;
		const element = target as FullscreenElement;

		if (
			document.fullscreenElement ||
			doc.webkitFullscreenElement ||
			doc.mozFullScreenElement ||
			doc.msFullscreenElement
		) {
			return;
		}

		try {
			if (target.requestFullscreen) {
				await target.requestFullscreen();
			} else if (element.webkitRequestFullscreen) {
				await element.webkitRequestFullscreen();
			} else if (element.mozRequestFullScreen) {
				await element.mozRequestFullScreen();
			} else if (element.msRequestFullscreen) {
				await element.msRequestFullscreen();
			} else {
				throw new Error("Fullscreen API not supported");
			}
			disableFullscreenFallback();
		} catch {
			enableFullscreenFallback();
		}
	}, [disableFullscreenFallback, enableFullscreenFallback]);

	const draw = useCallback(() => {
		const canvas = canvasRef.current;
		const image = imageRef.current;
		const parent = canvas?.parentElement;
		if (!(canvas && image && parent)) {
			return;
		}

		const ctx = canvas.getContext("2d");
		if (!ctx) {
			return;
		}

		const container: Size = {
			width: parent.clientWidth,
			height: parent.clientHeight,
		};
		const imageSize: Size = { width: image.width, height: image.height };
		const dpr = window.devicePixelRatio || 1;

		canvas.width = container.width * dpr;
		canvas.height = container.height * dpr;
		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		ctx.clearRect(0, 0, container.width, container.height);

		const transform = getContainTransform(container, imageSize);
		if (!transform) {
			return;
		}

		ctx.drawImage(
			image,
			transform.offsetX,
			transform.offsetY,
			transform.drawW,
			transform.drawH
		);

		for (const marker of markers) {
			const screen = imageRectToScreenContain(marker, container, imageSize);
			if (!screen) {
				continue;
			}
			drawDamageRect(ctx, {
				...screen,
				letter: marker.letter,
				color: marker.color,
				selected: marker.id === selectedId,
			});
		}

		if (draftScreen) {
			const draft = normalizeScreenDraft(draftScreen.start, draftScreen.end);
			drawDamageRect(ctx, {
				...draft,
				letter: selectedType.type,
				color: selectedType.color,
			});
		}
	}, [draftScreen, markers, selectedId, selectedType]);

	const drawRef = useRef(draw);
	drawRef.current = draw;

	useEffect(() => {
		let objectUrl = "";
		let cancelled = false;
		const image = new Image();

		image.onload = () => {
			if (cancelled) {
				return;
			}
			imageRef.current = image;
			setIsImageReady(true);
			drawRef.current();
			requestAnimationFrame(() => {
				enterFullscreen().catch(ignoreAsyncError);
			});
		};
		objectUrl = URL.createObjectURL(img);
		image.src = objectUrl;

		setMarkers([]);
		setSelectedId(null);
		setDraftScreen(null);
		setIsSaving(false);
		setIsImageReady(false);

		return () => {
			cancelled = true;
			URL.revokeObjectURL(objectUrl);
			imageRef.current = null;
		};
	}, [img, enterFullscreen]);

	useEffect(() => {
		draw();
	}, [draw]);

	useEffect(() => {
		const parent = canvasRef.current?.parentElement;
		if (!parent) {
			return;
		}

		const resizeObserver = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (!entry) {
				return;
			}
			const { width, height } = entry.contentRect;
			setContainerSize({ width, height });
			drawRef.current();
		});

		resizeObserver.observe(parent);
		setContainerSize({
			width: parent.clientWidth,
			height: parent.clientHeight,
		});

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	useEffect(() => {
		if (!containerRef.current) {
			return;
		}
		enterFullscreen().catch(ignoreAsyncError);
	}, [enterFullscreen]);

	const handlePointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
		if (!isLandscapeRef.current || isSaving) {
			return;
		}

		const canvas = canvasRef.current;
		const image = imageRef.current;
		if (!(canvas && image)) {
			return;
		}

		onInteraction();
		canvas.setPointerCapture(event.pointerId);

		const container = containerSizeRef.current;
		const imageSize = getImageSize(image);
		const rawPoint = getCanvasPoint(event, canvas);
		const clamped =
			clampScreenPointToVisibleImageContain(rawPoint, container, imageSize) ??
			rawPoint;
		startScreenRef.current = clamped;

		const imagePoint = screenToImageCoordsContain(
			clamped,
			container,
			imageSize
		);
		const hit = imagePoint
			? findMarkerAtImage(markersRef.current, imagePoint)
			: null;

		if (hit) {
			interactionRef.current = "select";
			pendingSelectIdRef.current = hit.id;
			setDraftScreen(null);
			return;
		}

		pendingSelectIdRef.current = null;
		interactionRef.current = "draw";
		setSelectedId(null);
		setDraftScreen({ start: clamped, end: clamped });
	};

	const handlePointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
		if (interactionRef.current !== "draw") {
			return;
		}

		const canvas = canvasRef.current;
		const image = imageRef.current;
		if (!(canvas && image)) {
			return;
		}

		const container = containerSizeRef.current;
		const imageSize = getImageSize(image);
		const rawPoint = getCanvasPoint(event, canvas);
		const clamped =
			clampScreenPointToVisibleImageContain(rawPoint, container, imageSize) ??
			rawPoint;

		setDraftScreen((previous) =>
			previous
				? { ...previous, end: clamped }
				: { start: startScreenRef.current, end: clamped }
		);
	};

	const handlePointerUp = (event: ReactPointerEvent<HTMLCanvasElement>) => {
		const mode = interactionRef.current;
		interactionRef.current = null;

		const canvas = canvasRef.current;
		const image = imageRef.current;
		if (!(canvas && image)) {
			setDraftScreen(null);
			return;
		}

		const container = containerSizeRef.current;
		const imageSize = getImageSize(image);
		const rawPoint = getCanvasPoint(event, canvas);

		if (mode === "select") {
			const pendingId = pendingSelectIdRef.current;
			pendingSelectIdRef.current = null;
			if (isTap(startScreenRef.current, rawPoint) && pendingId) {
				setSelectedId(pendingId);
			}
			return;
		}

		setDraftScreen(null);
		if (mode !== "draw") {
			return;
		}

		const clamped =
			clampScreenPointToVisibleImageContain(rawPoint, container, imageSize) ??
			rawPoint;
		const marker = createMarkerFromDraft({
			start: startScreenRef.current,
			end: clamped,
			container,
			imageSize,
			type: selectedTypeRef.current,
		});
		if (!marker) {
			return;
		}

		setMarkers((previous) => [...previous, marker]);
	};

	const handlePointerCancel = () => {
		interactionRef.current = null;
		pendingSelectIdRef.current = null;
		setDraftScreen(null);
	};

	const handleDeleteSelected = () => {
		if (!selectedId) {
			return;
		}
		setMarkers((previous) =>
			previous.filter((marker) => marker.id !== selectedId)
		);
		setSelectedId(null);
	};

	const handleSelectType = (type: DamageAnnotationType) => {
		setSelectedType(type);
		setSelectedId(null);
	};

	const handleSave = async () => {
		const image = imageRef.current;
		if (!image || isSaving) {
			return;
		}

		const damages = markers.map((marker) => marker.damageType);
		if (markers.length === 0) {
			onSave(createAnnotatedFile(img, fileName, damages));
			onExit();
			return;
		}

		setIsSaving(true);
		try {
			const blob = await bakeAnnotatedJpeg(
				image,
				markers,
				EXPORT_JPEG_QUALITY,
				EXPORT_MAX_LONG_EDGE
			);
			onSave(createAnnotatedFile(blob, fileName, damages));
			onExit();
		} catch {
			onSave(createAnnotatedFile(img, fileName, damages));
			onExit();
		} finally {
			setIsSaving(false);
		}
	};

	const selectedScreenRect = useMemo(() => {
		if (!(selectedId && isImageReady && imageRef.current)) {
			return null;
		}

		const marker = markers.find((item) => item.id === selectedId);
		if (!marker) {
			return null;
		}

		return imageRectToScreenContain(marker, containerSize, {
			width: imageRef.current.width,
			height: imageRef.current.height,
		});
	}, [containerSize, isImageReady, markers, selectedId]);

	const deletePromptPosition =
		selectedScreenRect && containerSize.width > 0
			? getDeletePromptPosition(selectedScreenRect, containerSize)
			: null;

	const isConfirmDisabled = !isImageReady || isSaving;

	return (
		<div
			className="relative h-screen w-screen overflow-hidden bg-black"
			data-slot="image-annotation"
			ref={containerRef}
		>
			<canvas
				aria-label="Área para marcar avarias na foto"
				className="h-full w-full cursor-crosshair touch-none"
				onPointerCancel={handlePointerCancel}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={handlePointerUp}
				ref={canvasRef}
			/>

			{selectedId !== null && deletePromptPosition ? (
				<button
					aria-label="Excluir marcador"
					className="absolute z-20 rounded-full bg-white px-3.5 py-2 font-semibold text-[#D92D20] text-[13px] shadow-lg"
					onClick={handleDeleteSelected}
					style={{
						left: deletePromptPosition.x,
						top: deletePromptPosition.y,
					}}
					type="button"
				>
					Excluir marcador
				</button>
			) : null}

			{legendCollapsed ? (
				<button
					aria-label="Mostrar legenda"
					className="fixed right-4 bottom-4 z-30 flex size-8 items-center justify-center rounded-lg bg-primary text-secondary shadow-lg hover:bg-primary/90"
					onClick={() => setLegendCollapsed(false)}
					type="button"
				>
					<ChevronUp className="size-5" />
				</button>
			) : (
				<div className="fixed bottom-0 left-0 z-30 flex w-full flex-wrap items-center justify-between gap-2 overflow-visible bg-primary/50 px-3 py-2">
					<div className="flex gap-2 overflow-x-auto">
						{DAMAGE_ANNOTATION_TYPES.map((item) => (
							<div
								className={cx(
									"flex flex-col items-center rounded px-1 py-0.5",
									selectedType.damageType === item.damageType && "bg-quaternary"
								)}
								key={item.damageType}
							>
								<button
									aria-label={item.description}
									aria-pressed={selectedType.damageType === item.damageType}
									className="flex size-7 items-center justify-center rounded-full border-2 bg-transparent"
									onClick={() => handleSelectType(item)}
									style={{ borderColor: item.color }}
									type="button"
								>
									<span
										className="flex size-5 items-center justify-center rounded-full bg-white font-semibold text-[10px] leading-none"
										style={{ color: item.color }}
									>
										{item.type}
									</span>
								</button>
								<span className="mt-0.5 text-[10px] text-secondary italic leading-tight">
									{item.description}
								</span>
							</div>
						))}
					</div>

					<div className="relative size-8 shrink-0">
						<button
							aria-label="Recolher legenda para ver a imagem inteira"
							className="-translate-y-3/4 absolute top-0 left-0 flex size-8 items-center justify-center rounded-md bg-primary/90 text-secondary shadow-lg hover:bg-primary"
							onClick={() => setLegendCollapsed(true)}
							type="button"
						>
							<ChevronDown className="size-5" />
						</button>
					</div>

					<div className="flex shrink-0 items-center gap-2">
						<button
							aria-label="Tirar foto novamente"
							className="size-8 rounded-md bg-primary p-2"
							onClick={() => {
								setIsExitModalOpen(true);
							}}
							type="button"
						>
							<FlipBackward className="text-fg-quaternary" size={16} />
						</button>
						<button
							aria-label="Concluir"
							className="flex h-8 items-center gap-1 rounded-md bg-brand-solid px-3 disabled:opacity-50"
							disabled={isConfirmDisabled}
							onClick={() => {
								handleSave().catch(ignoreAsyncError);
							}}
							type="button"
						>
							<CheckCircle className="text-white" size={16} />
							<span className="font-semibold text-sm text-white">Concluir</span>
						</button>
					</div>
				</div>
			)}

			{isLandscape ? null : (
				<LandscapeWrapper message="Para finalizar as marcações, use o modo horizontal." />
			)}

			<ConfirmModal
				cancelLabel="Sair"
				confirmLabel="Continuar Checklist"
				description="A foto atual e os PINS feitos nela serão perdidos. Deseja realmente sair?"
				isOpen={isExitModalOpen}
				onCancel={() => {
					setIsExitModalOpen(false);
					onRetake();
				}}
				onClose={() => {
					setIsExitModalOpen(false);
				}}
				onConfirm={() => {
					setIsExitModalOpen(false);
				}}
				title="Deseja sair sem salvar?"
			/>
		</div>
	);
};
